import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
  Typography,
  Dialog,
  DialogContent,
  Select,
  InputLabel,
  MenuItem,
  FormControl,
  TextField,
  Grid,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import {
  ChevronDown,
  X as LucideX,
  Info,
  HelpCircle,
} from "lucide-react";

import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ChatiaDialogActions from "../ChatiaDialogActions";
import useFilterDropdownStyles from "../SharedStyles/useFilterDropdownStyles";

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark = theme.palette.type === "dark";
  return {
    dialogPaper: {
      ...(ds.dialogPaper || {}),
      background: isDark ? "#121212" : "#ffffff",
    },
    dialogContent: {
      padding: "16px 20px !important",
      ...theme.scrollbarStyles,
    },
    header: {
      ...(ds.header || {}),
      flexShrink: 0,
    },
    headerTitle: {
      ...(ds.headerTitle || {}),
    },
    closeButton: {
      backgroundColor: ds.closeButton?.backgroundColor || "rgba(244, 67, 54, 0.1)",
      color: ds.closeButton?.color || "#f44336",
      transition: ds.closeButton?.transition || "all 0.2s ease",
    },
    sectionTitle: {
      fontSize: 13,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      marginTop: 16,
      marginBottom: 4,
    },
    sectionDescription: {
      fontSize: 12,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      marginBottom: 8,
      lineHeight: 1.5,
    },
    infoBox: {
      marginTop: 4,
      marginBottom: 14,
      padding: "12px 14px",
      borderRadius: 10,
      background: isDark ? "rgba(33,150,243,0.08)" : "rgba(33,150,243,0.06)",
      border: `1px solid ${isDark ? "rgba(33,150,243,0.25)" : "rgba(33,150,243,0.20)"}`,
    },
    infoTitle: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 13,
      fontWeight: 600,
      color: isDark ? "#90caf9" : "#1565c0",
      marginBottom: 6,
    },
    infoText: {
      fontSize: 12,
      color: dsColors.textSecondary || (isDark ? "#cfd8dc" : "#37474f"),
      lineHeight: 1.55,
      "& strong": {
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      },
      "& ul": {
        margin: "4px 0 4px 18px",
        padding: 0,
      },
      "& li": {
        marginBottom: 2,
      },
    },
    fieldWithHelp: {
      display: "flex",
      alignItems: "center",
      gap: 4,
      width: "100%",
    },
    fieldWithHelpInner: {
      flex: 1,
      minWidth: 0,
    },
    helpIcon: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      verticalAlign: "middle",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      cursor: "help",
      opacity: 0.6,
      marginLeft: 4,
      transition: "opacity 0.15s ease, color 0.15s ease",
      "&:hover": {
        opacity: 1,
        color: theme.palette.primary.main,
      },
    },
    instructionsBox: {
      marginTop: 4,
    },
    instructionsAccordion: {
      backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#fafafa",
      "&::before": { display: "none" },
      boxShadow: "none",
      border: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
      borderRadius: "10px !important",
    },
    stepNumber: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 22,
      height: 22,
      borderRadius: "50%",
      backgroundColor: theme.palette.primary.main,
      color: "#fff",
      fontSize: 12,
      fontWeight: 700,
      marginRight: 10,
      flexShrink: 0,
    },
    stepItem: {
      display: "flex",
      alignItems: "flex-start",
      marginBottom: 10,
      lineHeight: 1.55,
      fontSize: 13,
      color: dsColors.textSecondary || (isDark ? "#cfd8dc" : "#37474f"),
    },
    codeBlock: {
      backgroundColor: isDark ? "rgba(0,0,0,0.35)" : "#eef0f2",
      padding: "10px 12px",
      borderRadius: 8,
      fontFamily: "monospace",
      fontSize: 12,
      overflowX: "auto",
      marginTop: 4,
      marginBottom: 8,
      wordBreak: "break-all",
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      border: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
    },
    tipBox: {
      borderRadius: 10,
      padding: "10px 12px",
      marginTop: 10,
      display: "flex",
      gap: 8,
      alignItems: "flex-start",
      background: isDark ? "rgba(76,175,80,0.10)" : "rgba(76,175,80,0.08)",
      border: `1px solid ${isDark ? "rgba(76,175,80,0.30)" : "rgba(76,175,80,0.25)"}`,
      fontSize: 12.5,
      color: isDark ? "#a5d6a7" : "#2e7d32",
      lineHeight: 1.5,
    },
  };
});

const IntegrationSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, i18n.t("validation.tooShort"))
    .max(50, i18n.t("validation.tooLong"))
    .required(i18n.t("validation.required")),
});

// Helper de campo com ícone de ajuda
const FieldWithHelp = ({ classes, help, children }) => (
  <div className={classes.fieldWithHelp}>
    <div className={classes.fieldWithHelpInner}>{children}</div>
    {help && (
      <Tooltip arrow placement="top" title={help}>
        <span className={classes.helpIcon}>
          <HelpCircle size={16} strokeWidth={2} />
        </span>
      </Tooltip>
    )}
  </div>
);

const QueueIntegration = ({ open, onClose, integrationId }) => {
  const classes = useStyles();
  const dropdownClasses = useFilterDropdownStyles();

  const initialState = {
    type: "typebot",
    name: "",
    projectName: "",
    jsonContent: "",
    language: "",
    urlN8N: "",
    typebotDelayMessage: 1000,
    typebotExpires: 1,
    typebotKeywordFinish: "",
    typebotKeywordRestart: "",
    typebotRestartMessage: "",
    typebotSlug: "",
    typebotUnknownMessage: "",
  };

  const [integration, setIntegration] = useState(initialState);

  useEffect(() => {
    (async () => {
      if (!integrationId) return;
      try {
        const { data } = await api.get(`/queueIntegration/${integrationId}`);
        setIntegration((prevState) => ({ ...prevState, ...data }));
      } catch (err) {
        toastError(err);
      }
    })();

    return () => {
      setIntegration(initialState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationId, open]);

  const handleClose = () => {
    onClose();
    setIntegration(initialState);
  };

  const handleSaveIntegration = async (values) => {
    try {
      values.projectName = values.name;
      if (integrationId) {
        await api.put(`/queueIntegration/${integrationId}`, values);
        toast.success(i18n.t("queueIntegrationModal.messages.editSuccess"));
      } else {
        await api.post("/queueIntegration", values);
        toast.success(i18n.t("queueIntegrationModal.messages.addSuccess"));
      }
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      PaperProps={{ className: classes.dialogPaper }}
    >
      <div className={classes.header}>
        <Typography className={classes.headerTitle}>
          {integrationId
            ? i18n.t("queueIntegrationModal.title.edit")
            : i18n.t("queueIntegrationModal.title.add")}
        </Typography>
        <IconButton
          size="small"
          onClick={handleClose}
          className={classes.closeButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.18)";
            e.currentTarget.style.color = "#ffffff";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.08)";
            e.currentTarget.style.color = "#ffffff";
          }}
        >
          <LucideX size={18} strokeWidth={2.5} />
        </IconButton>
      </div>

      <Formik
        initialValues={integration}
        enableReinitialize={true}
        validationSchema={IntegrationSchema}
        onSubmit={(values, actions) => {
          setTimeout(() => {
            handleSaveIntegration(values);
            actions.setSubmitting(false);
          }, 400);
        }}
      >
        {({ touched, errors, isSubmitting, values }) => (
          <Form>
            <DialogContent dividers className={classes.dialogContent}>
              {/* INFO BOX EXPLICANDO INTEGRAÇÕES */}
              <div className={classes.infoBox}>
                <div className={classes.infoTitle}>
                  <Info size={15} strokeWidth={2.2} />
                  {i18n.t("queueIntegrationModal.info.intro.title") ||
                    "ⓘ O que são integrações de fluxo"}
                </div>
                <div className={classes.infoText}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html:
                        i18n.t("queueIntegrationModal.info.intro.text") ||
                        "Integrações conectam esta conexão WhatsApp a sistemas externos de automação:<ul><li><strong>N8N:</strong> orquestrador de workflows visual — você cria automações com gatilhos, condições e ações que são acionadas a cada mensagem recebida.</li><li><strong>Typebot:</strong> construtor de chatbots conversacionais — o cliente conversa com um fluxo guiado de perguntas e respostas pré-definido.</li></ul><strong>Como funciona:</strong> ao receber uma mensagem, o sistema envia os dados para a URL configurada e processa a resposta retornada. A integração é selecionada por conexão na aba <em>Integrações</em> do whatsapp.",
                    }}
                  />
                </div>
              </div>

              {/* SEÇÃO: TIPO DE INTEGRAÇÃO */}
              <div className={classes.sectionTitle}>
                {i18n.t("queueIntegrationModal.sections.type") || "Tipo de integração"}
              </div>
              <div className={classes.sectionDescription}>
                {i18n.t("queueIntegrationModal.sections.typeDesc") ||
                  "Escolha o serviço externo que vai processar as mensagens."}
              </div>

              <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                  <FormControl variant="outlined" margin="dense" fullWidth>
                    <InputLabel id="type-selection-input-label">
                      {i18n.t("queueIntegrationModal.form.type")}
                    </InputLabel>
                    <Field
                      as={Select}
                      label={i18n.t("queueIntegrationModal.form.type")}
                      name="type"
                      labelId="type-selection-input-label"
                      id="type"
                      required
                      MenuProps={{
                        classes: { paper: dropdownClasses.paper },
                        anchorOrigin: { vertical: "bottom", horizontal: "left" },
                        transformOrigin: { vertical: "top", horizontal: "left" },
                        getContentAnchorEl: null,
                      }}
                    >
                      <MenuItem value="n8n">N8N</MenuItem>
                      <MenuItem value="typebot">Typebot</MenuItem>
                    </Field>
                  </FormControl>
                </Grid>
              </Grid>

              {/* N8N */}
              {values.type === "n8n" && (
                <>
                  <div className={classes.sectionTitle}>
                    {i18n.t("queueIntegrationModal.sections.n8nConfig") ||
                      "Configuração do N8N"}
                  </div>
                  <div className={classes.sectionDescription}>
                    {i18n.t("queueIntegrationModal.sections.n8nConfigDesc") ||
                      "Informe um nome para identificar esta integração e a URL do webhook que receberá os eventos."}
                  </div>

                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6}>
                      <FieldWithHelp
                        classes={classes}
                        help={
                          i18n.t("queueIntegrationModal.help.name") ||
                          "Nome interno para identificar esta integração na lista (ex: 'Bot atendimento N8N')."
                        }
                      >
                        <Field
                          as={TextField}
                          label={i18n.t("queueIntegrationModal.form.name")}
                          autoFocus
                          required
                          name="name"
                          error={touched.name && Boolean(errors.name)}
                          helperText={touched.name && errors.name}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </FieldWithHelp>
                    </Grid>
                    <Grid item xs={12}>
                      <FieldWithHelp
                        classes={classes}
                        help={
                          i18n.t("queueIntegrationModal.help.urlN8N") ||
                          "URL completa do webhook do seu workflow no N8N. Encontre em: nó Webhook → 'Production URL'. Deve ser publicamente acessível pelo backend desta plataforma."
                        }
                      >
                        <Field
                          as={TextField}
                          label={i18n.t("queueIntegrationModal.form.urlN8NWebhook")}
                          name="urlN8N"
                          error={touched.urlN8N && Boolean(errors.urlN8N)}
                          helperText={touched.urlN8N && errors.urlN8N}
                          variant="outlined"
                          margin="dense"
                          required
                          fullWidth
                          placeholder="https://seu-n8n.com/webhook/abc123"
                        />
                      </FieldWithHelp>
                    </Grid>
                    <Grid item xs={12}>
                      <Box className={classes.instructionsBox}>
                        <Accordion className={classes.instructionsAccordion}>
                          <AccordionSummary expandIcon={<ChevronDown size={18} />}>
                            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                              {i18n.t("queueIntegrationModal.instructions.n8nTitle")}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Box style={{ width: "100%" }}>
                              {[1, 2, 3, 4, 5].map((n) => (
                                <div className={classes.stepItem} key={n}>
                                  <span className={classes.stepNumber}>{n}</span>
                                  <span>
                                    {i18n.t(`queueIntegrationModal.instructions.n8nStep${n}`)}
                                  </span>
                                </div>
                              ))}

                              <Typography variant="subtitle2" style={{ fontWeight: 600, marginTop: 16, marginBottom: 8 }}>
                                {i18n.t("queueIntegrationModal.instructions.n8nDataTitle")}
                              </Typography>
                              <div className={classes.codeBlock}>
                                {"{"}<br />
                                &nbsp;&nbsp;{"\"message\": { \"text\": \"...\", \"type\": \"conversation\" },"}<br />
                                &nbsp;&nbsp;{"\"contact\": { \"name\": \"...\", \"number\": \"5511...\" },"}<br />
                                &nbsp;&nbsp;{"\"ticket\": { \"id\": 123, \"status\": \"pending\" },"}<br />
                                &nbsp;&nbsp;{"\"apiToken\": \"token-para-responder\","}<br />
                                &nbsp;&nbsp;{"\"apiUrl\": \"https://seu-backend.com\""}<br />
                                {"}"}
                              </div>

                              <Typography variant="subtitle2" style={{ fontWeight: 600, marginTop: 16, marginBottom: 8 }}>
                                {i18n.t("queueIntegrationModal.instructions.n8nReplyTitle")}
                              </Typography>
                              <div className={classes.stepItem}>
                                <span>{i18n.t("queueIntegrationModal.instructions.n8nReplyOption1")}</span>
                              </div>
                              <div className={classes.codeBlock}>
                                {"{ \"reply\": \"Sua resposta aqui\" }"}
                              </div>
                              <div className={classes.stepItem}>
                                <span>{i18n.t("queueIntegrationModal.instructions.n8nReplyOption2")}</span>
                              </div>
                              <div className={classes.codeBlock}>
                                {"POST {{ apiUrl }}/api/messages/send"}<br />
                                {"Authorization: Bearer {{ apiToken }}"}<br />
                                {"{ \"number\": \"5511...\", \"body\": \"Resposta\" }"}
                              </div>

                              <div className={classes.tipBox}>
                                <Info size={15} strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 1 }} />
                                <span>
                                  {i18n.t("queueIntegrationModal.instructions.n8nTip")}
                                </span>
                              </div>
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      </Box>
                    </Grid>
                  </Grid>
                </>
              )}

              {/* TYPEBOT */}
              {values.type === "typebot" && (
                <>
                  <div className={classes.sectionTitle}>
                    {i18n.t("queueIntegrationModal.sections.typebotConnection") ||
                      "Conexão com o Typebot"}
                  </div>
                  <div className={classes.sectionDescription}>
                    {i18n.t("queueIntegrationModal.sections.typebotConnectionDesc") ||
                      "Identifique a integração e informe o servidor + slug do bot publicado no Typebot."}
                  </div>

                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6}>
                      <FieldWithHelp
                        classes={classes}
                        help={
                          i18n.t("queueIntegrationModal.help.name") ||
                          "Nome interno para identificar esta integração na lista (ex: 'FAQ Typebot Comercial')."
                        }
                      >
                        <Field
                          as={TextField}
                          label={i18n.t("queueIntegrationModal.form.name")}
                          autoFocus
                          name="name"
                          error={touched.name && Boolean(errors.name)}
                          helperText={touched.name && errors.name}
                          variant="outlined"
                          margin="dense"
                          required
                          fullWidth
                        />
                      </FieldWithHelp>
                    </Grid>
                    <Grid item xs={12}>
                      <FieldWithHelp
                        classes={classes}
                        help={
                          i18n.t("queueIntegrationModal.help.urlTypebot") ||
                          "URL base do servidor Typebot (ex: 'https://typebot.io' para a versão oficial ou o domínio da sua instância self-hosted)."
                        }
                      >
                        <Field
                          as={TextField}
                          label={i18n.t("queueIntegrationModal.form.urlN8N")}
                          name="urlN8N"
                          error={touched.urlN8N && Boolean(errors.urlN8N)}
                          helperText={touched.urlN8N && errors.urlN8N}
                          variant="outlined"
                          margin="dense"
                          required
                          fullWidth
                          placeholder="https://typebot.io"
                        />
                      </FieldWithHelp>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FieldWithHelp
                        classes={classes}
                        help={
                          i18n.t("queueIntegrationModal.help.typebotSlug") ||
                          "Identificador público do bot publicado. Encontre na aba Share do seu Typebot — é a parte final da URL (ex: 'meu-bot-123')."
                        }
                      >
                        <Field
                          as={TextField}
                          label={i18n.t("queueIntegrationModal.form.typebotSlug")}
                          name="typebotSlug"
                          error={touched.typebotSlug && Boolean(errors.typebotSlug)}
                          helperText={touched.typebotSlug && errors.typebotSlug}
                          required
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </FieldWithHelp>
                    </Grid>
                  </Grid>

                  <div className={classes.sectionTitle}>
                    {i18n.t("queueIntegrationModal.sections.typebotBehavior") ||
                      "Comportamento da conversa"}
                  </div>
                  <div className={classes.sectionDescription}>
                    {i18n.t("queueIntegrationModal.sections.typebotBehaviorDesc") ||
                      "Controla tempo de expiração, delay entre mensagens e palavras-chave que encerram ou reiniciam o bot."}
                  </div>

                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6}>
                      <FieldWithHelp
                        classes={classes}
                        help={
                          i18n.t("queueIntegrationModal.help.typebotExpires") ||
                          "Tempo (em horas) que a sessão do bot fica ativa sem nova mensagem do contato. Após esse período, o fluxo reinicia. Use 0 para nunca expirar."
                        }
                      >
                        <Field
                          as={TextField}
                          label={i18n.t("queueIntegrationModal.form.typebotExpires")}
                          name="typebotExpires"
                          type="number"
                          error={touched.typebotExpires && Boolean(errors.typebotExpires)}
                          helperText={touched.typebotExpires && errors.typebotExpires}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </FieldWithHelp>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FieldWithHelp
                        classes={classes}
                        help={
                          i18n.t("queueIntegrationModal.help.typebotDelayMessage") ||
                          "Tempo (em ms) que o bot espera antes de enviar cada resposta. Simula digitação humana. Recomendado: 1000-2000ms."
                        }
                      >
                        <Field
                          as={TextField}
                          label={i18n.t("queueIntegrationModal.form.typebotDelayMessage")}
                          name="typebotDelayMessage"
                          type="number"
                          error={touched.typebotDelayMessage && Boolean(errors.typebotDelayMessage)}
                          helperText={touched.typebotDelayMessage && errors.typebotDelayMessage}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </FieldWithHelp>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FieldWithHelp
                        classes={classes}
                        help={
                          i18n.t("queueIntegrationModal.help.typebotKeywordFinish") ||
                          "Palavra que, quando enviada pelo cliente, encerra imediatamente o fluxo do bot e libera o atendimento humano (ex: 'sair', 'atendente')."
                        }
                      >
                        <Field
                          as={TextField}
                          label={i18n.t("queueIntegrationModal.form.typebotKeywordFinish")}
                          name="typebotKeywordFinish"
                          error={touched.typebotKeywordFinish && Boolean(errors.typebotKeywordFinish)}
                          helperText={touched.typebotKeywordFinish && errors.typebotKeywordFinish}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </FieldWithHelp>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FieldWithHelp
                        classes={classes}
                        help={
                          i18n.t("queueIntegrationModal.help.typebotKeywordRestart") ||
                          "Palavra que reinicia o fluxo do bot do zero (ex: 'reiniciar', 'menu'). O cliente volta para a primeira pergunta do Typebot."
                        }
                      >
                        <Field
                          as={TextField}
                          label={i18n.t("queueIntegrationModal.form.typebotKeywordRestart")}
                          name="typebotKeywordRestart"
                          error={touched.typebotKeywordRestart && Boolean(errors.typebotKeywordRestart)}
                          helperText={touched.typebotKeywordRestart && errors.typebotKeywordRestart}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </FieldWithHelp>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FieldWithHelp
                        classes={classes}
                        help={
                          i18n.t("queueIntegrationModal.help.typebotUnknownMessage") ||
                          "Mensagem que o bot envia quando recebe uma resposta que não entende ou que não bate com nenhuma opção do fluxo (ex: 'Não entendi, pode reformular?')."
                        }
                      >
                        <Field
                          as={TextField}
                          label={i18n.t("queueIntegrationModal.form.typebotUnknownMessage")}
                          name="typebotUnknownMessage"
                          error={touched.typebotUnknownMessage && Boolean(errors.typebotUnknownMessage)}
                          helperText={touched.typebotUnknownMessage && errors.typebotUnknownMessage}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </FieldWithHelp>
                    </Grid>
                    <Grid item xs={12}>
                      <FieldWithHelp
                        classes={classes}
                        help={
                          i18n.t("queueIntegrationModal.help.typebotRestartMessage") ||
                          "Mensagem enviada automaticamente quando o fluxo é reiniciado pela palavra-chave acima (ex: 'Vamos começar de novo!')."
                        }
                      >
                        <Field
                          as={TextField}
                          label={i18n.t("queueIntegrationModal.form.typebotRestartMessage")}
                          name="typebotRestartMessage"
                          error={touched.typebotRestartMessage && Boolean(errors.typebotRestartMessage)}
                          helperText={touched.typebotRestartMessage && errors.typebotRestartMessage}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </FieldWithHelp>
                    </Grid>
                  </Grid>
                </>
              )}
            </DialogContent>

            <ChatiaDialogActions
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
              saveText={
                integrationId
                  ? i18n.t("queueIntegrationModal.buttons.okEdit")
                  : i18n.t("queueIntegrationModal.buttons.okAdd")
              }
            />
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default QueueIntegration;
